import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LinkOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, List, Tag, Typography } from 'antd';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '../../api/client';
import { moyskladApi, skladsApi, storesApi } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import { ModalSelect } from '../../components/ModalSelect';

/**
 * MoySklad connection tab (SPEC §1 integration): token/webhook status, this
 * business's source sklad, and the store ↔ counterparty mapping. One MoySklad
 * account feeds several businesses; a counterparty held elsewhere is shown
 * but cannot be picked. The token itself lives
 * in env on the server — this UI only reports whether it works.
 */
export function MoyskladTab(): JSX.Element {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const status = useQuery({ queryKey: queryKeys.moyskladStatus, queryFn: moyskladApi.status });
  const enabled = status.data?.tokenConfigured === true;

  const webhooks = useQuery({
    queryKey: queryKeys.moyskladWebhooks,
    queryFn: moyskladApi.webhooks,
    enabled,
  });
  const counterparties = useQuery({
    queryKey: queryKeys.moyskladCounterparties,
    queryFn: moyskladApi.counterparties,
    enabled,
    staleTime: 5 * 60_000,
  });
  const sklads = useQuery({ queryKey: queryKeys.sklads(), queryFn: () => skladsApi.list() });
  const stores = useQuery({ queryKey: queryKeys.stores(), queryFn: () => storesApi.list() });

  const onError = (error: Error): void => {
    message.error(apiErrorMessage(error) ?? t('common.requestError'));
  };

  const setSkladMutation = useMutation({
    mutationFn: (skladId: string | null) => moyskladApi.setSklad(skladId),
    onSuccess: () => {
      message.success(t('common.saved'));
      void queryClient.invalidateQueries({ queryKey: ['moysklad'] });
    },
    onError,
  });

  const mapStoreMutation = useMutation({
    mutationFn: ({
      storeId,
      msCounterpartyId,
    }: {
      storeId: string;
      msCounterpartyId: string | null;
    }) => storesApi.update(storeId, { msCounterpartyId }),
    onSuccess: () => {
      message.success(t('common.saved'));
      void queryClient.invalidateQueries({ queryKey: ['stores'] });
      void queryClient.invalidateQueries({ queryKey: ['moysklad'] });
    },
    onError,
  });

  const registerMutation = useMutation({
    mutationFn: moyskladApi.registerWebhooks,
    onSuccess: (result) => {
      message.success(
        result.isRegistered ? t('moysklad.webhooksRegistered') : t('moysklad.webhooksPartial'),
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.moyskladWebhooks });
    },
    onError,
  });

  const okTag = (label: string): JSX.Element => (
    <Tag icon={<CheckCircleOutlined />} color="green">
      {label}
    </Tag>
  );
  const badTag = (label: string): JSX.Element => (
    <Tag icon={<CloseCircleOutlined />} color="red">
      {label}
    </Tag>
  );

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Card size="small" title={t('moysklad.statusTitle')} loading={status.isLoading}>
        {status.data ? (
          <div style={{ display: 'grid', gap: 8 }}>
            <div>
              {status.data.tokenConfigured
                ? status.data.tokenValid
                  ? okTag(t('moysklad.tokenOk'))
                  : badTag(t('moysklad.tokenInvalid'))
                : badTag(t('moysklad.tokenMissing'))}
              {status.data.webhookSecretConfigured ? null : badTag(t('moysklad.secretMissing'))}
              {enabled ? (
                webhooks.data?.isRegistered ? (
                  okTag(t('moysklad.webhooksOk'))
                ) : (
                  <Tag icon={<LinkOutlined />} color="orange">
                    {t('moysklad.webhooksNotRegistered')}
                  </Tag>
                )
              ) : null}
            </div>
            {!status.data.tokenConfigured ? (
              <Typography.Text type="secondary">{t('moysklad.tokenHint')}</Typography.Text>
            ) : null}
            {enabled ? (
              <Button
                icon={<LinkOutlined />}
                onClick={() => registerMutation.mutate()}
                loading={registerMutation.isPending}
                disabled={!status.data.webhookSecretConfigured}
              >
                {t('moysklad.registerWebhooks')}
              </Button>
            ) : null}
          </div>
        ) : status.isError ? (
          <Typography.Text type="danger">{t('common.loadError')}</Typography.Text>
        ) : null}
      </Card>

      <Card size="small" title={t('moysklad.skladTitle')}>
        <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
          {t('moysklad.skladHint')}
        </Typography.Paragraph>
        <ModalSelect
          size="large"
          style={{ width: '100%' }}
          placeholder={t('common.skladRequired')}
          title={t('moysklad.skladTitle')}
          value={status.data?.skladId ?? undefined}
          loading={sklads.isLoading || setSkladMutation.isPending}
          options={(sklads.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
          onChange={(value) => value && setSkladMutation.mutate(value)}
          allowClear
          onClear={() => setSkladMutation.mutate(null)}
        />
      </Card>

      <Card
        size="small"
        title={t('moysklad.mappingTitle')}
        extra={
          enabled ? (
            <Button
              type="text"
              icon={<ReloadOutlined />}
              onClick={() => void counterparties.refetch()}
              loading={counterparties.isFetching}
              aria-label={t('common.retry')}
            />
          ) : null
        }
      >
        <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
          {t('moysklad.mappingHint')}
        </Typography.Paragraph>
        {!enabled ? (
          <Typography.Text type="secondary">{t('moysklad.mappingDisabled')}</Typography.Text>
        ) : (
          <List
            dataSource={stores.data ?? []}
            loading={stores.isLoading}
            renderItem={(store) => (
              // gridTemplateColumns 1fr: an auto track would shrink the
              // 100%-wide Select to its content width.
              <List.Item
                style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6, paddingInline: 0 }}
              >
                <Typography.Text strong>{store.name}</Typography.Text>
                <ModalSelect
                  size="large"
                  style={{ width: '100%' }}
                  placeholder={t('moysklad.counterpartyPlaceholder')}
                  title={store.name}
                  value={store.msCounterpartyId ?? undefined}
                  loading={counterparties.isLoading}
                  options={(counterparties.data ?? []).map((c) => ({
                    value: c.id,
                    label: c.name,
                    ...(c.takenBy
                      ? { description: t('moysklad.takenBy', { where: c.takenBy }), disabled: true }
                      : null),
                  }))}
                  onChange={(value) =>
                    value && mapStoreMutation.mutate({ storeId: store.id, msCounterpartyId: value })
                  }
                  allowClear
                  onClear={() =>
                    mapStoreMutation.mutate({ storeId: store.id, msCounterpartyId: null })
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>
    </div>
  );
}
